"use client";

import { X } from "lucide-react";
import { type MouseEvent, type ReactNode, useEffect } from "react";
import { Button } from "./button";

export function Modal({
  title,
  description,
  open,
  onClose,
  children,
  bodyOverflow = "auto",
  footer,
  headerActions,
  titleContent,
  height = "fixed",
  size = "lg",
}: {
  title: string;
  description?: string;
  open: boolean;
  onClose: () => void;
  children: ReactNode;
  footer?: ReactNode;
  headerActions?: ReactNode;
  titleContent?: ReactNode;
  bodyOverflow?: "auto" | "hidden";
  height?: "auto" | "fixed";
  size?: "md" | "lg" | "xl" | "wide" | "full";
}) {
  useEffect(() => {
    if (!open) {
      return;
    }

    function handleKeyDown(event: KeyboardEvent) {
      if (event.key === "Escape") {
        onClose();
      }
    }

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [onClose, open]);

  if (!open) {
    return null;
  }

  const width = {
    md: "max-w-lg",
    lg: "max-w-3xl",
    xl: "max-w-5xl",
    wide: "max-w-7xl",
    full: "max-w-none",
  }[size];
  const heightClass =
    size === "full"
      ? "h-[calc(100dvh-1rem)]"
      : height === "fixed"
        ? "h-[min(46rem,calc(100dvh-2rem))] sm:h-[min(52rem,calc(100dvh-2rem))]"
        : "max-h-[calc(100dvh-2rem)]";

  function handleBackdropMouseDown(event: MouseEvent<HTMLDivElement>) {
    if (event.target === event.currentTarget) {
      onClose();
    }
  }

  return (
    <div
      className={`fixed inset-0 z-50 grid place-items-center bg-slate-950/45 ${size === "full" ? "px-2 py-2" : "px-4 py-4"}`}
      onMouseDown={handleBackdropMouseDown}
    >
      <section
        className={`flex ${heightClass} w-full ${width} flex-col overflow-hidden ${size === "full" ? "rounded-xl" : "rounded-lg"} bg-white shadow-xl dark:bg-slate-950`}
        role="dialog"
        aria-modal="true"
        aria-label={title}
      >
        <header className="flex items-center justify-between gap-4 border-b border-slate-200 px-5 py-3 dark:border-slate-800">
          <div className="min-w-0 flex-1">
            <h2 className="min-w-0 truncate text-lg font-semibold text-slate-950 dark:text-slate-50">
              {titleContent ?? title}
            </h2>
            {description ? (
              <p className="mt-1 truncate text-sm text-slate-500 dark:text-slate-400">
                {description}
              </p>
            ) : null}
          </div>
          <div className="flex shrink-0 flex-wrap items-center justify-end gap-2">
            {headerActions}
            <Button
              type="button"
              variant="ghost"
              size="icon"
              onClick={onClose}
              aria-label="Fechar modal"
            >
              <X className="h-5 w-5" aria-hidden="true" />
            </Button>
          </div>
        </header>
        <div
          className={`min-h-0 flex-1 ${
            bodyOverflow === "hidden" ? "overflow-hidden" : "overflow-y-auto"
          } p-4 sm:p-5`}
        >
          {children}
        </div>
        {footer ? (
          <footer className="shrink-0 border-t border-slate-200 bg-white px-4 py-2.5 dark:border-slate-800 dark:bg-slate-950 sm:px-5">
            {footer}
          </footer>
        ) : null}
      </section>
    </div>
  );
}
